/* ──────────────────────────────────────────────────────────
   Main user dashboard page.

   Layout:
     [ Top nav with sign-out ]
     [ Greeting ]
     [ Main column                    | Right rail            ]
     [   UserProfile (identity+KPIs)  |   Welcome modal       ]
     [   Roadmap                      |   New User Checklist  ]
     [   Plans (free-tier only)       |   Upgrade card        ]
     [   Websites                     |                       ]
     [   Vision Questionnaire         |                       ]
     [   Kick-Off Scheduler           |                       ]
     [   Contract                     |                       ]
     [   Invoices                     |                       ]
     [   File Storage                 |                       ]

   The Plans section uses a negative top margin and a thin
   connector line so it reads as a continuation of the roadmap
   above when it does appear (unactivated users).

   First paint: 2.8s 0→100% loader (brand colors).
   ────────────────────────────────────────────────────────── */
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "./api";
import { useAuth } from "./AuthContext";
import Loader from "./Loader";
import { WelcomeModal, NewUserChecklist, UpgradeCard } from "./Sidebar";
import { Roadmap, VisionQuestionnaire, Contract, Invoices, KickOffScheduler, FileStorage, Websites, Plans } from "./Sections";
import UserProfile from "./UserProfile";

const C = {
  bg: "#111111",
  bgSecondary: "#171717",
  glass: "rgba(255,255,255,0.06)",
  glassStrong: "rgba(255,255,255,0.10)",
  borderSubtle: "rgba(255,255,255,0.08)",
  borderMedium: "rgba(255,255,255,0.14)",
  text: "#F5F5F5",
  textSecondary: "rgba(255,255,255,0.72)",
  textMuted: "rgba(255,255,255,0.48)",
  accent: "#E5D3A3",
};

export default function Dashboard() {
  const { user, hasActiveAccess, effectiveStatus, refreshFromStripe, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const [showLoader, setShowLoader] = useState(true);
  const [hasIntake, setHasIntake] = useState(false);
  const [hasMeeting, setHasMeeting] = useState(false);

  // If returning from Stripe checkout, refresh status + clean URL.
  useEffect(() => {
    if (params.get("checkout") === "success") {
      refreshFromStripe();
      const next = new URLSearchParams(params); next.delete("checkout"); setParams(next, { replace: true });
    }
  }, [params]);

  // Compute checklist completion flags.
  useEffect(() => {
    if (!user) return;
    api.get("/intake").then((r) => setHasIntake(!!r.data.submission)).catch(() => {});
    api.get("/meetings").then((r) => setHasMeeting((r.data.upcoming || []).length > 0)).catch(() => {});
  }, [user, hasActiveAccess]);

  if (showLoader) return <Loader onDone={() => setShowLoader(false)} label={user ? `WELCOME, ${user.firstName.toUpperCase()}` : "PREPARING YOUR SPACE"} />;
  if (!user) return null; // guard handled by route wrapper

  return (
    <>
      <WelcomeModal />

      <nav style={N.nav}>
        <Link to="/" style={N.brand}>
          <div style={N.brandTitle}>Digital Billions Group</div>
          <div style={N.brandSub}>Member Dashboard</div>
        </Link>
        <div style={N.right}>
          <span style={N.who}>◌ {user.firstName} {user.lastName}</span>
          <button onClick={logout} style={N.signOut}>Sign out</button>
        </div>
      </nav>

      <div style={{ background: "#000000", minHeight: "100vh" }}>
      <div style={W.shell}>
        {/* Greeting */}
        <header style={{ marginBottom: 56 }}>
          <p style={W.eyebrow}>◈ &nbsp; {greetingFor(user)} &nbsp; ·&nbsp; {statusLabel(effectiveStatus)}</p>
          <h1 style={W.h1}>Hello, <em style={{ fontStyle: "italic", color: "rgba(255,255,255,0.72)" }}>{user.firstName}.</em></h1>
          <p style={W.lede}>{leadeFor(hasActiveAccess)}</p>
        </header>

        <div style={W.grid}>
          <main style={W.main}>
            <UserProfile />
            <Roadmap />
            <Plans />
            <Websites />
            <VisionQuestionnaire />
            <KickOffScheduler />
            <Contract />
            <Invoices />
            <FileStorage />
          </main>

          <aside style={W.aside}>
            <NewUserChecklist hasActiveAccess={hasActiveAccess} hasIntake={hasIntake} hasMeeting={hasMeeting} />
            <UpgradeCard />
          </aside>
        </div>

        <footer style={W.footer}>
          <span style={W.eyebrow}>◈ DIGITAL BILLIONS GROUP &nbsp; ◌ &nbsp; MMXXII—MMXXVI</span>
          <span style={W.eyebrow}>REFINE · AUTOMATE · CREATE</span>
        </footer>
      </div>
      </div>
    </>
  );
}

function greetingFor() {
  const h = new Date().getHours();
  if (h < 5) return "GOOD EVENING";
  if (h < 12) return "GOOD MORNING";
  if (h < 18) return "GOOD AFTERNOON";
  return "GOOD EVENING";
}
function leadeFor(active) {
  return active
    ? "Here's where everything lives — your roadmap, projects, files, and conversations with us."
    : "Take a look around. When you're ready, activate your membership to begin.";
}

// User-facing status string. The tone-of-voice rules ask us to
// never say "unpaid" or "past due" bluntly to the user; we map the
// raw status enum to gentler labels here in one place.
function statusLabel(status) {
  switch (status) {
    case "active":   return "MEMBER";
    case "explore":  return "EXPLORE";
    case "past_due": return "ACCOUNT ATTENTION";
    case "canceled": return "INACTIVE";
    default:         return String(status || "").toUpperCase();
  }
}

const N = {
  nav: {
    position: "fixed", top: 0, left: 0, right: 0, zIndex: 40,
    background: "rgba(17,17,17,0.85)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderBottom: `1px solid rgba(255,255,255,0.08)`,
    padding: "14px 40px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
  },
  brand: { textDecoration: "none", color: C.text },
  brandTitle: { fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif", fontSize: 14, fontWeight: 500, letterSpacing: "0.02em", color: C.text },
  brandSub: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: C.textMuted, marginTop: 2 },
  right: { display: "flex", gap: 16, alignItems: "center" },
  who: { fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.18em", color: C.textMuted },
  signOut: {
    fontFamily: "'DM Mono', monospace", fontSize: 10, letterSpacing: "0.18em",
    color: C.textMuted, background: "rgba(255,255,255,0.06)",
    border: `1px solid rgba(255,255,255,0.10)`,
    borderRadius: 999, cursor: "pointer", textTransform: "uppercase",
    padding: "6px 14px",
  },
};

const W = {
  shell: { maxWidth: 1240, margin: "0 auto", padding: "100px 40px 80px", background: "#000000", minHeight: "100vh" },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: 9, letterSpacing: "0.28em", textTransform: "uppercase", color: C.textMuted, marginBottom: 16 },
  h1: {
    fontFamily: "'Inter', 'DM Sans', system-ui, sans-serif",
    fontWeight: 300, fontSize: 52, lineHeight: 1.1,
    color: C.text, letterSpacing: "-0.02em", marginBottom: 14,
  },
  lede: { fontSize: 15, color: C.textSecondary, lineHeight: 1.7, maxWidth: 560 },
  grid: { display: "grid", gridTemplateColumns: "1fr 300px", gap: 40, alignItems: "start" },
  main: { minWidth: 0 },
  aside: { display: "flex", flexDirection: "column", gap: 12, position: "sticky", top: 84 },
  footer: { marginTop: 64, paddingTop: 20, borderTop: `1px solid rgba(255,255,255,0.08)`, display: "flex", justifyContent: "space-between" },
};
